"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createClient } from "@/lib/supabase";
import { AnimateSection, StaggerContainer, StaggerItem } from "@/components/AnimateSection";
import { useProfile } from "@/lib/use-portfolio-data";

function buildSocialLinks(profile: { github_url?: string | null; linkedin_url?: string | null; facebook_url?: string | null; youtube_url?: string | null; twitter_url?: string | null; instagram_url?: string | null }) {
  const links = [];
  if (profile.github_url) {
    links.push({
      label: "GitHub", href: profile.github_url, desc: "Check out my repos",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
      ),
      color: "hover:text-white hover:border-white/20",
    });
  }
  if (profile.linkedin_url) {
    links.push({
      label: "LinkedIn", href: profile.linkedin_url, desc: "Connect with me",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      color: "hover:text-[#0A66C2] hover:border-[#0A66C2]/30",
    });
  }
  if (profile.facebook_url) {
    links.push({
      label: "Facebook", href: profile.facebook_url, desc: "Follow me",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      color: "hover:text-[#1877F2] hover:border-[#1877F2]/30",
    });
  }
  if (profile.youtube_url) {
    links.push({
      label: "YouTube", href: profile.youtube_url, desc: "Watch my videos",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
      color: "hover:text-[#FF0000] hover:border-[#FF0000]/30",
    });
  }
  if (profile.twitter_url) {
    links.push({
      label: "X", href: profile.twitter_url, desc: "Follow me on X",
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      color: "hover:text-white hover:border-white/20",
    });
  }
  if (profile.instagram_url) {
    links.push({
      label: "Instagram", href: profile.instagram_url, desc: "See my photos",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      ),
      color: "hover:text-[#E4405F] hover:border-[#E4405F]/30",
    });
  }
  return links;
}

export default function ContactPage() {
  const { profile } = useProfile();
  const socialLinks = profile ? buildSocialLinks(profile) : [];
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    const supabase = createClient();
    const { error } = await supabase.from("contact_messages").insert({
      name: form.name,
      email: form.email,
      message: form.message,
    });
    if (error) {
      setStatus("error");
      return;
    }
    setStatus("success");
    setForm({ name: "", email: "", message: "" });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
      <AnimateSection>
        <h1 className="text-4xl font-bold text-dev-text-primary sm:text-5xl">
          Get In <span className="text-gradient">Touch</span>
        </h1>
        <p className="mt-4 max-w-xl text-dev-text-muted">
          Have a question or want to work together? Feel free to reach out!
        </p>
      </AnimateSection>

      <div className="mt-12 grid gap-8 lg:grid-cols-7">
        {/* Contact Form */}
        <AnimateSection className="lg:col-span-4" delay={0.1}>
          <div className="clay-card p-8 gradient-border h-full">
            <h2 className="mb-6 text-xl font-semibold text-dev-text-primary">
              Send a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-dev-text-secondary">
                    Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="clay-input w-full px-4 py-3 text-sm text-dev-text-primary placeholder-dev-text-muted"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-dev-text-secondary">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="clay-input w-full px-4 py-3 text-sm text-dev-text-primary placeholder-dev-text-muted"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="mb-2 block text-sm font-medium text-dev-text-secondary">
                  Message
                </label>
                <textarea
                  id="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  className="clay-input w-full px-4 py-3 text-sm text-dev-text-primary placeholder-dev-text-muted resize-none"
                  placeholder="Tell me about your project..."
                />
              </div>
              <button
                type="submit"
                disabled={status === "sending"}
                className="btn-gradient flex w-full items-center justify-center gap-2 py-3.5 font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {status === "sending" ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                      </svg>
                    </>
                  )}
                </span>
              </button>
            </form>
            <AnimatePresence mode="wait">
              {status === "success" && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 flex items-center gap-2 rounded-xl bg-dev-emerald/10 border border-dev-emerald/20 px-4 py-3 text-sm text-dev-emerald"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Message sent successfully!
                </motion.div>
              )}
              {status === "error" && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 flex items-center gap-2 rounded-xl bg-rose-500/10 border border-rose-500/20 px-4 py-3 text-sm text-rose-400"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  Something went wrong. Please try again.
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </AnimateSection>

        {/* Contact Info Sidebar */}
        <AnimateSection className="lg:col-span-3" delay={0.2}>
          <div className="clay-card p-6 gradient-border h-full flex flex-col">
            <h2 className="mb-4 text-lg font-semibold text-dev-text-primary">📬 Contact Info</h2>

            {/* Direct Contact */}
            <div className="space-y-1">
              {profile?.email && (
                <a href={`mailto:${profile.email}`} className="group flex items-center gap-3 rounded-lg p-2.5 transition-all duration-300 hover:bg-white/5">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-dev-cyan/10 text-dev-cyan">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-dev-text-muted">Email</p>
                    <p className="text-sm font-medium text-dev-text-primary group-hover:text-dev-cyan transition-colors truncate">{profile.email}</p>
                  </div>
                </a>
              )}
              {profile?.phone && (
                <a href={`tel:${profile.phone.replace(/\s/g, "")}`} className="group flex items-center gap-3 rounded-lg p-2.5 transition-all duration-300 hover:bg-white/5">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-dev-emerald/10 text-dev-emerald">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-dev-text-muted">Phone</p>
                    <p className="text-sm font-medium text-dev-text-primary group-hover:text-dev-emerald transition-colors">{profile.phone}</p>
                  </div>
                </a>
              )}
              {profile?.address && (
                <div className="flex items-center gap-3 rounded-lg p-2.5">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-dev-purple/10 text-dev-purple">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-dev-text-muted">Location</p>
                    <p className="text-sm font-medium text-dev-text-primary">{profile.address}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Divider + Social Links */}
            {socialLinks.length > 0 && (
              <>
                <div className="my-4 border-t border-white/5" />
                <div className="grid grid-cols-2 gap-2">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group flex items-center gap-2.5 rounded-lg border border-white/5 p-3 transition-all duration-300 hover:bg-white/5 ${social.color}`}
                    >
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/5 text-dev-text-muted transition-colors group-hover:bg-white/10">
                        {social.icon}
                      </div>
                      <span className="text-sm font-medium text-dev-text-primary transition-colors">{social.label}</span>
                    </a>
                  ))}
                </div>
              </>
            )}

            {/* Availability */}
            <div className="mt-auto pt-4 border-t border-white/5 mt-4">
              <div className="flex items-center gap-3">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-dev-emerald opacity-75"></span>
                  <span className="inline-flex h-3 w-3 rounded-full bg-dev-emerald"></span>
                </span>
                <div>
                  <h3 className="font-medium text-dev-text-primary text-sm">Available for work</h3>
                  <p className="text-xs text-dev-text-muted">Open for freelance & full-time</p>
                </div>
              </div>
            </div>
          </div>
        </AnimateSection>
      </div>
    </div>
  );
}
